import { create } from 'zustand';
import type {
  ArtifactContent,
  ChatAttachment,
  ProfileUpdateEvent,
  TrackingProtocolProposalEvent,
  MealPlan,
  DeepResearchResult,
  GoalPlan,
  ProposedRecord,
  ProposedRecordStatus,
  ProposedLabRecord,
} from '@researcher/shared';

export interface HealthAnalysisResult {
  symptom: string;
  hypothesis: string | null;
  summary: string;
  insights: Array<{
    type: string;
    confidence: string;
    headline: string;
    explanation: string;
  }>;
  insightCount: number;
}

export interface SourceCitation {
  title: string;
  source: string;
  sourceUrl: string;
  collection: 'knowledgeBase' | 'sourceDocuments' | 'topics';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: SourceCitation[];
  artifact?: ArtifactContent;
  artifactId?: string;
  researchCard?: DeepResearchResult;
  communitySuggest?: string;
  communityPostSuggest?: { postId: string; postTitle: string };
  profileUpdate?: ProfileUpdateEvent;
  profileUpdateStatus?: 'applied' | 'dismissed';
  trialSuggest?: string;
  artifactSuggest?: { type: string; label: string };
  researchSuggest?: { query: string; focusArea: string; label: string };
  healthAnalysis?: HealthAnalysisResult;
  goalPlan?: { goalId: string; goalDescription: string; plan: GoalPlan };
  mealPlan?: MealPlan;
  reminderCreated?: { id: string; title: string; time: string; frequency: string; daysOfWeek?: number[]; notificationsEnabled?: boolean; action?: 'created' | 'updated' | 'deleted' | 'disabled' | 'enabled' };
  protocolEnrolled?: { id: string; condition: string; conditionLabel: string; items: string[]; schedule: { frequency: string; time: string; timezone: string } };
  trackingProtocolProposal?: TrackingProtocolProposalEvent;
  trackingProtocolStatus?: 'applied' | 'dismissed';
  protocolUpdated?: { id: string; status: string; action: string };
  preferenceSet?: { id: string; label: string; action: 'created' | 'updated' | 'deleted' };
  suggestedReplies?: string[];
  isNudge?: boolean;
  attachments?: ChatAttachment[];
  /** Local-only object URLs keyed by storagePath, populated when the user just
   *  uploaded a file in this session — lets the bubble show a thumbnail without
   *  re-fetching from Storage. Not persisted in chat history. */
  attachmentPreviewUrls?: Record<string, string>;
  /** Structured records Romy proposed from an attachment. Renders a
   *  confirmation card on the assistant message. Saved status + any edits
   *  live alongside so the card survives re-renders. */
  proposedRecords?: {
    summary: string;
    sourceDescription: string;
    records: ProposedRecord[];
  };
  proposedRecordStatuses?: Record<string, ProposedRecordStatus>;
  proposedRecordEdits?: Record<string, ProposedLabRecord>;
}

interface ChatSessionState {
  messages: ChatMessage[];
  currentChatId: string | null;
  input: string;
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setCurrentChatId: (id: string | null) => void;
  setInput: (input: string | ((prev: string) => string)) => void;
  reset: () => void;
}

export const useChatSession = create<ChatSessionState>((set) => ({
  messages: [],
  currentChatId: null,
  input: '',
  setMessages: (messages) =>
    set((state) => ({
      messages: typeof messages === 'function' ? messages(state.messages) : messages,
    })),
  setCurrentChatId: (currentChatId) => set({ currentChatId }),
  setInput: (input) =>
    set((state) => ({
      input: typeof input === 'function' ? input(state.input) : input,
    })),
  reset: () =>
    set((state) => {
      if (state.messages.length === 0 && state.currentChatId === null && state.input === '') {
        return state; // no-op, avoid new reference
      }
      return { messages: [], currentChatId: null, input: '' };
    }),
}));
